import css from "./standart.module.css";
import headerLogoOne from "../../img/headerLogoOne.png";
import headerLogoTwo from "../../img/headerLogoTwo.png";
import { logOut } from "../../lib/logOut";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import axios from "axios";
import { fetchCSRFToken, getCSRFToken } from "../../lib/csrf";
import SubModal from "./SubModal";
import { pollSubscriptionStatus } from "../../lib/subscriptionStatusApi";

const Header = ({ authUser, pageTheme, isPricing }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [showLangPopup, setShowLangPopup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isLightMode, setIsLightMode] = useState(true);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isSubModalOpen, setIsSubModalOpen] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState(null);

  const languages = [
    { code: "en", label: "EN" },
    { code: "uk", label: "UA" },
    { code: "pl", label: "PL" },
  ];

  useEffect(() => {
   if(pageTheme === 'dark') {
    setIsLightMode(false);
   } else {
    setIsLightMode(true);
   }
  }, [])

  // Fetch current subscription when user is authenticated
  const fetchCurrentSubscription = async () => {
    if (!authUser?.isAuthenticated) return;

    let activeSubscription = null;

    // 1) Try orders endpoint first (fast)
    try {
      const response = await axios.get("/api/billing/orders/", {
        withCredentials: true,
      });

      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        activeSubscription = response.data.find((sub) => Number(sub.status) === 2) || null;
      }
    } catch (error) {
      console.error("Error fetching current subscription (orders):", error);
    }

    if (activeSubscription) {
      setCurrentSubscription(activeSubscription);
      return;
    }

    // 2) Fallback to subscription/status polling endpoint (authoritative)
    try {
      const result = await pollSubscriptionStatus(5, 2000);
      if (result && typeof result === "object" && Number(result.status) === 2) {
        setCurrentSubscription(result);
      } else {
        setCurrentSubscription(result);
      }
    } catch (error) {
      console.error("Error fetching current subscription (poll):", error);
      setCurrentSubscription(null);
    }
  };

  useEffect(() => {
    fetchCurrentSubscription();
  }, [authUser?.isAuthenticated]);

  const isHomePage = location.pathname === '/';

  const handleNavigate = (path, options = {}) => {
    navigate(path, options);
    window.scrollTo(0, 0);
  };

  // Scroll behavior for home page
  /*useEffect(() => {
    if (!isHomePage) {
      setIsLightMode(true);
      setScrollProgress(0);
      return;
    }

    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const progress = (scrollTop / scrollHeight) * 100;
      setScrollProgress(progress);

      const heroHeight = window.innerHeight;
      const bottomBlock = document.querySelector('[class*="benefits"]');

      let shouldBeLightMode = scrollTop > heroHeight * 0.75;

      // Check if header is touching or almost touching the bottom block
      if (bottomBlock) {
        const bottomBlockRect = bottomBlock.getBoundingClientRect();
        const headerHeight = 80; // Approximate header height
        const threshold = 50; // Distance threshold for "almost touching"

        // If bottom block top is close to or touching the header bottom
        if (bottomBlockRect.top <= headerHeight + threshold) {
          shouldBeLightMode = false;
        }
      }

      setIsLightMode(shouldBeLightMode);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHomePage]);*/

  const changeLanguage = async (lang) => {
    setLoading(true);
    try {
      i18n.changeLanguage(lang);
      localStorage.setItem("selectedLanguage", lang);

      if (authUser && authUser.isAuthenticated === true) {
        let csrfToken = getCSRFToken();
        if (!csrfToken) {
          await fetchCSRFToken();
          csrfToken = getCSRFToken();
        }

        await axios.patch(
          "/api/v1/user-profile/current",
          { language: lang },
          {
            headers: {
              "Content-Type": "application/json",
              "X-CSRFToken": csrfToken,
            },
            withCredentials: true,
          }
        );
      }

      setShowLangPopup(false);
    } catch (error) {
      console.error("Помилка зміни мови:", error.response?.data || error);
      setShowLangPopup(false);
    } finally {
      setLoading(false);
    }
  };

  const getLanguageLabel = (code) => {
    if (code === "uk") return "UA";
    return (
      languages.find((lang) => lang.code === code)?.label || code.toUpperCase()
    );
  };

  const handleLogout = async () => {
    const success = await logOut();
    if (success) {
      setTimeout(() => {
        window.location.href = "/signin";
      }, 300)
    }
  };

  return (
    <>
      {/* Scroll Progress Bar - only on home page */}
      {isHomePage && (
        <div className={css.scrollProgress} style={{ width: `${scrollProgress}%` }}></div>
      )}

      <header className={`${css.headerWrap} ${isLightMode ? css.lightMode : ''}`}>
        <div className={css.headerContent}>
          <Link
            to="/"
            className={css.logo}
            onClick={(e) => {
              e.preventDefault();
              if (location.pathname === '/') {
                // If already on home page, just scroll
                const videoDiscoverySection = document.querySelector('[class*="videoDiscoverySection"]');
                if (videoDiscoverySection) {
                  const headerHeight = 75; // Fixed header height
                  const elementPosition = videoDiscoverySection.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - headerHeight - 20; // 20px extra padding
                  
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                }
              } else {
                 sessionStorage.setItem("fromOnboarding", "true");
                 navigate("/", {
                  replace: true,})
              }
              // If not on home page, let the normal navigation happen
              // This will cause a clean navigation to / without any state
            }}
            style={{ cursor: 'pointer' }}
          >
            <div className={css.logoIcon}>AI</div>
            <span>AI Lingua</span>
          </Link>
          <div onClick={() => setIsOpen(!isOpen)} className={`${css.menuIcon} ${isOpen ? css.active : ''}`}>
            <span></span>
          </div>
          <nav className={`${css.navActions}  ${isOpen ? css.active : ''}`}>
            <div className={css.langSwitcher}>
              {languages.map(({ code, label }) => (
                <button
                  key={code}
                  className={`${css.langBtn} ${i18n.language === code ? css.active : ''}`}
                  onClick={() => changeLanguage(code)}
                  disabled={loading}
                >
                  {loading && i18n.language === code ? "⏳" : label}
                </button>
              ))}
            </div>

            <div className={css.authButtons}>
              {!isPricing && (<button 
              className={css.pricingBtn}
               onClick={() => navigate('/pricing')}>
                {t("Pricing")}
               </button>)}

              <button 
                className={css.subscriptionBtn}
                onClick={() => setIsSubModalOpen(true)}
              >
                {t("Subscription")}
              </button>

              {authUser && authUser.isAuthenticated === true ? (
                <>
                  <button
                    className={css.btnSecondary}
                    onClick={() => navigate('/')}
                  >
                    {t("My learning")}
                  </button>
                  <button className={css.btnPrimary} onClick={handleLogout}>
                    {t("logout")}
                  </button>
                </>
              ) : (
                <>
                  <button
                    className={css.btnSecondary}
                    onClick={() => handleNavigate("/signin")}
                  >
                    {t("signin")}
                  </button>
                  <button
                    className={css.btnPrimary}
                    onClick={() => handleNavigate("/signup")}
                  >
                    {t("register")}
                  </button>
                </>
              )}
            </div>
          </nav>
        </div>
      </header>
      
      {/* Subscription Modal */}
      <SubModal 
        isOpen={isSubModalOpen} 
        onClose={() => setIsSubModalOpen(false)} 
        authUser={authUser}
        currentSubscription={currentSubscription}
        onSubscriptionCancelled={fetchCurrentSubscription}
      />
    </>
  );
};

export default Header;
